import { memo, useState, useEffect, ChangeEvent } from 'react';

import MonitorSettingsForm from './MonitorSettingsForm';
import MonitorList from './MonitorList';
import GreenButton from '../../components/GreenButton';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';

import {
  freqValue,
  freqUnit,
  getFrequencyFromSnapshotIndex,
  getIndexFromSnapshotFrequency,
  getCenterWithLo,
} from '../../utils/FreqUtils';

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  SelectChangeEvent,
} from '@mui/material';

import { UpdateSnapshotMonitor } from '../monitors/types';
import { InvalidEntryFlags, MonitorForm } from './types';
import InfoPopup from '../../components/InfoPopup';

import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useMonitor } from 'features/monitors/api/getMonitor';
import { useCreateMonitor } from 'features/monitors/api/createMonitor';
import { useUpdateMonitor } from 'features/monitors/api/updateMonitor';
import { useDeleteMonitor } from 'features/monitors/api/deleteMonitor';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';
import DraggablePaper from 'components/DraggablePaper';

interface Props {
  traceLength: number;
  open: boolean;
  handleClose: () => void;
  handleRedrawMonitor: (
    monitorId: number,
    detectorType: InterferenceDetector,
    detectorName?: string | undefined
  ) => void;
  selectedSnapshotId: number;
  isHistoryMode?: boolean;
}

const invalidEntryFlagsInitialState = {
  begin: false,
  end: false,
  upperbound: false,
  lowerbound: false,
};

const MonitorConfigurationDialog = memo(
  ({
    traceLength,
    open,
    handleClose,
    handleRedrawMonitor,
    selectedSnapshotId,
  }: Props) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidEntryFlags, setInvalidEntryFlags] =
      useState<InvalidEntryFlags>(invalidEntryFlagsInitialState);

    const [selectedMonitorId, setSelectedMonitorId] = useState(-1);
    const [monitor, setMonitor] = useState<MonitorForm>({
      name: '',
      beginFrequencyValue: '',
      beginFrequencyUnit: 'MHz',
      endFrequencyValue: '',
      endFrequencyUnit: 'MHz',
      upperbound: '',
      lowerbound: '',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { data: snapshot } = useSnapshot({ snapshotId: selectedSnapshotId });
    const { monitors } = snapshot || {};

    const { data: selectedMonitor } = useMonitor({
      snapshotId: selectedSnapshotId,
      monitorId: selectedMonitorId,
    });

    const createMonitorMutation = useCreateMonitor({
      snapshotId: selectedSnapshotId,
    });
    const updateMonitorMutation = useUpdateMonitor({
      snapshotId: selectedSnapshotId,
    });
    const deleteMonitorMutation = useDeleteMonitor({
      snapshotId: selectedSnapshotId,
    });

    useEffect(() => {
      if (
        selectedMonitorId === -1 ||
        monitors?.length === 0 ||
        !selectedMonitor
      ) {
        setMonitor({
          name: '',
          beginFrequencyValue: '',
          beginFrequencyUnit: 'MHz',
          endFrequencyValue: '',
          endFrequencyUnit: 'MHz',
          upperbound: '',
          lowerbound: '',
        });
        return;
      }

      if (!traceLength || !snapshot) return;

      const beginFrequencyIndex = selectedMonitor.begin;
      const endFrequencyIndex = selectedMonitor.end;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequencyIndex
      );
      const endFrequency = getFrequencyFromSnapshotIndex(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequencyIndex
      );

      const beginFrequencyValue = freqValue(beginFrequency);
      const beginFrequencyUnit = freqUnit(beginFrequency);

      const endFrequencyValue = freqValue(endFrequency);
      const endFrequencyUnit = freqUnit(endFrequency);

      const parsedMonitor = {
        ...selectedMonitor,
        beginFrequencyValue: beginFrequencyValue,
        beginFrequencyUnit: beginFrequencyUnit,
        endFrequencyValue: endFrequencyValue,
        endFrequencyUnit: endFrequencyUnit,
        upperbound: selectedMonitor.upperbound / 1000,
        lowerbound: selectedMonitor.lowerbound / 1000,
      };

      setMonitor(parsedMonitor);
    }, [
      selectedMonitorId,
      selectedMonitor,
      traceLength,
      snapshot,
      snapshot?.center,
      snapshot?.span,
      monitors,
    ]);

    const handleCloneMonitorClicked = async () => {
      if (selectedMonitor)
        await createMonitorMutation.mutateAsync({
          data: {
            ...selectedMonitor,
            warning: true,
            name: selectedMonitor.name + ' (Warning)',
          },
          snapshotId: selectedSnapshotId,
        });
    };

    const handleMonitorChanged = (
      event:
        | SelectChangeEvent<string>
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        checked?: boolean
    ) => {
      const { name, value } = event.target;
      if (name === undefined) return;
      const valueToSet = name === 'warning' ? checked : value;

      setMonitor({
        ...monitor,
        [name]: valueToSet,
      });
      setHasChanges(true);

      setInvalidEntryFlags(invalidEntryFlagsInitialState);
    };

    const handleSubmitClicked = async () => {
      if (!snapshot) return;

      const beginFrequency =
        monitor.beginFrequencyValue + ' ' + monitor.beginFrequencyUnit;
      const endFrequency =
        monitor.endFrequencyValue + ' ' + monitor.endFrequencyUnit;
      const centerWithLo = getCenterWithLo(
        snapshot.center,
        snapshot.lo,
        snapshot.lo_mode
      );

      const beginIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        beginFrequency
      );
      const endIndex = getIndexFromSnapshotFrequency(
        centerWithLo,
        snapshot.span,
        traceLength,
        endFrequency
      );

      // Validate inputs and set error flags accordingly
      const newFlags: InvalidEntryFlags = {
        begin: false,
        end: false,
        upperbound: false,
        lowerbound: false,
      };
      if (beginIndex === -1) newFlags.begin = true;
      if (endIndex === -1) newFlags.end = true;
      const graphMax = snapshot.reference;
      const graphMin = -(10 * snapshot.scale) + snapshot.reference;
      if (
        Number(monitor.upperbound) > graphMax ||
        Number(monitor.upperbound) < graphMin ||
        Number(monitor.upperbound) < Number(monitor.lowerbound)
      )
        newFlags.upperbound = true;
      if (
        Number(monitor.lowerbound) > graphMax ||
        Number(monitor.lowerbound) < graphMin ||
        Number(monitor.upperbound) < Number(monitor.lowerbound)
      )
        newFlags.lowerbound = true;

      if (
        newFlags.begin ||
        newFlags.end ||
        newFlags.lowerbound ||
        newFlags.upperbound
      ) {
        setInvalidEntryFlags(newFlags);
        return;
      }

      if (beginIndex >= endIndex) {
        setInvalidEntryFlags((flags) => ({ ...flags, begin: true, end: true }));
        return;
      }

      if (monitor.id === undefined || monitor.snapshot_id === undefined) return;

      const updatedMonitor: UpdateSnapshotMonitor = {
        id: monitor.id,
        snapshot_id: monitor.snapshot_id,
        name: monitor.name,
        begin: beginIndex,
        end: endIndex,
        upperbound: Math.round(Number(monitor.upperbound) * 1000),
        lowerbound: Math.round(Number(monitor.lowerbound) * 1000),
        warning: !!monitor.warning,
      };

      await updateMonitorMutation.mutateAsync({
        data: updatedMonitor,
        snapshotId: selectedSnapshotId,
        monitorId: selectedMonitorId,
      });

      setSelectedMonitorId(-1);
      setHasChanges(false);
    };

    const handleDeleteMonitorClicked = async () => {
      if (!selectedMonitor) return;

      await deleteMonitorMutation.mutateAsync({
        snapshotId: selectedSnapshotId,
        monitorId: selectedMonitorId,
      });

      setSelectedMonitorId(-1);
    };

    const handleNewMonitorClicked = async () => {
      if (!snapshot) return;
      const newMonitor = {
        snapshot_id: snapshot.id,
        name: 'New Monitor',
        begin: 0,
        end: 0,
        upperbound: -30 * 1000,
        lowerbound: -30 * 1000,
      };

      await createMonitorMutation.mutateAsync({
        data: newMonitor,
        snapshotId: selectedSnapshotId,
      });
    };

    const handleMonitorClicked = (monitorId: number) => {
      setSelectedMonitorId(monitorId);
      setInvalidEntryFlags(invalidEntryFlagsInitialState);
    };

    return (
      <>
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          itemToDelete={monitor.name}
          onSubmit={handleDeleteMonitorClicked}
          onClose={() => setDeleteDialogOpen(false)}
        />
        <Dialog
          open={open}
          onClose={handleClose}
          PaperComponent={DraggablePaper}
          hideBackdrop
          aria-labelledby='draggable-dialog-title'
        >
          <DialogTitle style={{ cursor: 'move' }} id='draggable-dialog-title'>
            Monitor Configuration
          </DialogTitle>
          <DialogContent>
            <Grid container>
              <Grid item xs={6}>
                <MonitorList
                  monitors={monitors}
                  selectedMonitorId={selectedMonitorId}
                  handleMonitorClicked={handleMonitorClicked}
                />
              </Grid>
              <Grid item xs={6}>
                <MonitorSettingsForm
                  monitor={monitor}
                  handleMonitorChanged={handleMonitorChanged}
                  invalidEntryFlags={invalidEntryFlags}
                  handleCloneMonitorClicked={handleCloneMonitorClicked}
                  disabled={!selectedMonitor}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSelectedMonitorId(-1);
                handleClose();
                setInvalidEntryFlags(invalidEntryFlagsInitialState);
              }}
              variant='contained'
              color='secondary'
            >
              Close
            </Button>
            <Button
              onClick={() => setDeleteDialogOpen(true)}
              color='error'
              variant='contained'
              disabled={selectedMonitorId === -1}
            >
              Delete Monitor
            </Button>
            <Button
              onClick={() =>
                handleRedrawMonitor(
                  selectedMonitorId,
                  InterferenceDetector.Monitor,
                  selectedMonitor?.name
                )
              }
              name='Redraw'
              color='primary'
              variant='contained'
              disabled={selectedMonitorId === -1}
            >
              Redraw
            </Button>
            <GreenButton
              onClick={handleNewMonitorClicked}
              name='New Monitor'
            ></GreenButton>
            <Button
              onClick={handleSubmitClicked}
              color='primary'
              variant='contained'
              disabled={selectedMonitorId === -1 || !hasChanges}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
        <InfoPopup
          openCondition={
            invalidEntryFlags.begin ||
            invalidEntryFlags.end ||
            invalidEntryFlags.upperbound ||
            invalidEntryFlags.lowerbound
          }
          title='Invalid Values'
          message='Your settings could not be saved due to an invalid begin, end, upperbound, or lowerbound value.'
          error
        />
      </>
    );
  }
);

export default MonitorConfigurationDialog;
